<template>
  <div id="top" class="auth-registry-page auth-content">
    <div class="auth-registry-intro">
      <UiPageHeader
        title="Authorizations"
        description="Review authorization requests and resolve clinical decisions from one focused workspace."
      >
        <template #actions>
          <router-link v-if="!selectedEncounter" :to="{ name: 'authAdd' }" class="auth-add-link">
            <FilePlus2 :size="17" aria-hidden="true" />
            <span>Add authorization</span>
          </router-link>
          <button v-else type="button" class="auth-all-link" @click="handleInlineClose">
            <ArrowLeft :size="17" aria-hidden="true" />
            <span>All Authorizations</span>
          </button>
        </template>
      </UiPageHeader>
    </div>

    <section class="auth-registry-workbench sba-slide-in" aria-label="Authorization request register">
      <AuthInlineReview
        ref="authInlineReview"
        :key="`${selectedEncounter?.id || 'none'}-${initialDetailState || 'review'}`"
        :encounter-id="selectedEncounter?.id"
        :just-created="isPostAddDetail"
        :initial-state="initialDetailState"
        @close="handleInlineClose"
        @saved="handleInlineSaved"
        v-if="selectedEncounter"
      />

      <template v-else>
        <div class="auth-registry-toolbar">
          <div class="auth-registry-summary" aria-live="polite">
            <strong v-if="isReady">
              {{ encounters.length }} {{ encounters.length === 1 ? "request" : "requests" }} in this view
            </strong>
            <strong v-else>Loading authorization requests</strong>
            <span>Clinical and operational review queue</span>
          </div>
          <button
            type="button"
            class="auth-filter-toggle"
            aria-controls="auth-filter-panel"
            :aria-expanded="showSearchFilters"
            @click="toggleSearchFilters"
          >
            <X v-if="showSearchFilters" :size="17" aria-hidden="true" />
            <ListFilter v-else :size="17" aria-hidden="true" />
            <span>{{ showSearchFilters ? "Close filters" : "Filter" }}</span>
          </button>
        </div>

        <div id="auth-filter-panel" class="auth-filter-shell" v-show="showSearchFilters">
          <div class="auth-filter-panel">
            <FilterAuth :key="filterResetKey" @search="handleSearch" />
          </div>
        </div>

        <div class="auth-current-view" :class="{ 'has-loader': isLoading }">
          <span class="summary-label">Current view</span>
          <div class="summary-content" v-if="searchTitle" v-html="searchTitle"></div>
          <div class="summary-content" v-else>
            <span class="summary-chip">All authorization activity</span>
          </div>
        </div>

        <div class="progress auth-progress" v-if="isReady && isLoading">
          <div class="indeterminate"></div>
        </div>

        <LoadingDiv v-if="!isReady" />

        <div class="auth-request-list" v-if="isReady && encounters.length">
          <div class="auth-request-head hide-on-med-and-down">
            <span>No.</span>
            <span>Member</span>
            <span>Provider</span>
            <span>Diagnosis</span>
            <span>Request</span>
            <span>Messages</span>
            <span aria-hidden="true"></span>
          </div>

          <article
            class="auth-request-row"
            :class="{ 'needs-review': hasPendingAuth(encounter) }"
            v-for="(encounter, index) in encounters"
            :key="encounter.id"
          >
            <div class="auth-request-grid">
              <div><span class="auth-number">{{ numberBadge(index) }}</span></div>

              <div class="auth-person">
                <h6>
                  <span>{{ encounter.name }}</span>
                  <span class="auth-type-chip is-principal" v-if="encounter.type == 'p'">Principal</span>
                  <span class="auth-type-chip" v-if="encounter.type == 's'">Spouse</span>
                  <span class="auth-type-chip" v-if="encounter.type == 'd'">Dependant</span>
                </h6>
                <div class="auth-contact-line">
                  <span>{{ showAgeSex(encounter?.dob, encounter?.sex)?.toUpperCase() }}</span>
                  <span class="contact-dot"></span>
                  <span>{{ encounter.phone || "No phone recorded" }}</span>
                </div>
                <div class="auth-id-line">
                  <span>Enrollee ID</span>
                  <strong>{{ encounter.enrollee_number }}</strong>
                </div>
                <p class="auth-client" v-if="encounter.client_name">{{ encounter.client_name }}</p>
              </div>

              <div class="auth-data-cell is-provider">
                <span>Provider</span>
                <p>{{ encounter.provider?.name || "No provider recorded" }}</p>
              </div>

              <div class="auth-data-cell is-diagnosis">
                <span>Diagnosis</span>
                <p>{{ encounter.diagnosis || "No diagnosis recorded" }}</p>
              </div>

              <div class="auth-data-cell is-status">
                <span class="auth-status-pill is-waiting" v-if="hasPendingAuth(encounter)">
                  <strong>Waiting</strong>
                  <span>{{ timeSince(encounter.claims) }}</span>
                </span>
                <span class="auth-status-pill is-clear" v-else>
                  <span>
                    {{ new Date(encounter.rank).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                    }) }}
                  </span>
                </span>
              </div>

              <div class="auth-message-cell">
                <button
                  type="button"
                  class="auth-message-action"
                  :class="{ 'has-unread': encounterUnreadMessageCount(encounter) }"
                  @click="handleMessages(encounter)"
                  :title="encounterMessageLabel(encounter)"
                  :aria-label="`${encounterMessageLabel(encounter)}. Open the Messages tab.`"
                >
                  <MessageSquare :size="16" aria-hidden="true" />
                  <span>{{ encounterMessageCount(encounter) }}</span>
                  <b class="auth-message-unread" v-if="encounterUnreadMessageCount(encounter)">
                    {{ encounterUnreadMessageCount(encounter) > 99 ? "99+" : encounterUnreadMessageCount(encounter) }}
                  </b>
                </button>
              </div>

              <div class="auth-action-cell">
                <button
                  type="button"
                  class="auth-request-action"
                  @click="handleEdit(encounter)"
                  :title="hasPendingAuth(encounter) ? 'Review request' : 'View request details'"
                  :aria-label="hasPendingAuth(encounter) ? 'Review request' : 'View request details'"
                >
                  <ChevronRight :size="18" aria-hidden="true" />
                </button>
              </div>
            </div>
          </article>
        </div>

        <div class="auth-empty-state" v-if="isReady && !isLoading && !encounters.length">
          <h3>No authorizations found</h3>
          <p>No authorization record matched the current selection.</p>
        </div>

        <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-else-if="encounters.length" />
      </template>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { ArrowLeft, ChevronRight, FilePlus2, ListFilter, MessageSquare, X } from "@lucide/vue";
import getItems from "@/composables/getItems";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";
import FilterAuth from "@/components/auth/FilterAuthComponent.vue";
import AuthInlineReview from "@/components/auth/AuthInlineReviewComponent.vue";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import { ageSex } from "@/composables/fxLibrary/coreFx.js";
import { getCache, addCache } from "@/composables/cache/cache";
import { siloMode } from '@/laravel/config';
import {
  dismissPersistentAuthorizationNotice,
  registerAuthorizationRealtimeTarget,
} from "@/composables/authorizationRealtimeCoordinator";
import { unreadEncounterMessageCount } from "@/composables/encounterMessageReadState";

export default {
  name: "authHome",
  components: {
    AuthInlineReview,
    ArrowLeft,
    ChevronRight,
    FilePlus2,
    FilterAuth,
    ListFilter,
    LoadingDiv,
    MessageSquare,
    PaginationDiv,
    UiPageHeader,
    X,
  },
  setup() {
    const response = ref(null);
    const searchTitle = ref(null);
    const hasActiveSearch = ref(false);
    const isReady = ref(false);
    const isLoading = ref(true);
    const isEmpty = ref(true);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const getResource = ref(null);
    const showSearchFilters = ref(false);
    const selectedEncounter = ref(null);
    const authInlineReview = ref(null);
    const pendingRealtimeListPage = ref(null);
    const filterResetKey = ref(0);
    // const homeUrl = ref(null);

    // router
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const isPostAddDetail = computed(() => route.query.created === "1" || route.query.created === 1);
    const initialDetailState = computed(() => String(route.query.tab || ""));
    const authListScope = () => store.state?.user?.auth_request_scope === "all" ? "all" : "active";
    const authListPerPage = (scope) => scope === "all" ? 100 : 25;
    const authSchemeId = (scheme) => Number(scheme?.id ?? scheme);
    const isValidAuthScheme = (scheme) => Number.isInteger(authSchemeId(scheme)) && authSchemeId(scheme) > 0;
    const defaultAuthSchemeId = () => {
      const schemes = (store.state?.user?.schemes || []).filter(isValidAuthScheme);
      const currentScheme = schemes.find(x => x.curr);

      return authSchemeId(currentScheme || schemes[0] || 0);
    };
    const authListUrl = (schemeId) => {
      const scope = authListScope();
      const perPage = authListPerPage(scope);
      const schemeQuery = siloMode ? `&s=${schemeId || 0}` : `&scheme_id=${schemeId || 0}`;

      return siloMode
        ? `encounter/auth/search?scope=${scope}&per_page=${perPage}${schemeQuery}`
        : `integration/oneprovider/auth?scope=${scope}&per_page=${perPage}${schemeQuery}`;
    };

    // start
    onMounted(() => {
      // page name
      document.title = "All Authorizations";
    });

    // sort silo
    onMounted(() => {
      // sort
      getResource.value = getItems;
    });

    const toggleSearchFilters = () => {
      dismissPersistentAuthorizationNotice(store);
      showSearchFilters.value = !showSearchFilters.value;
    };

    const scrollToPageTop = () => {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 50);
    };

    const hasPendingAuth = (encounter) => {
      return encounter?.has_pending_auth || encounter?.claims?.some(x => x.authorization === 'z' && x.status === 'a');
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // get time
    const timeSince = (val) => {
      // get first unresponded
      const useFirstSentDateTime = val.find(x => x.authorization == "z" && x.status == "a");

      // function
      const past = new Date(useFirstSentDateTime?.created_at);
      if (!useFirstSentDateTime?.created_at || Number.isNaN(past.getTime())) {
        return "Pending";
      }

      const now = new Date();
      const diffMs = now - past;

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''}`;

      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''}`;

      const diffDays = Math.floor(diffHrs / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }

    // get function
    const getItemsFromApi = (table, options = {}) => {
      const background = options.background === true;

      // get item from store (cache)
      let cache = background ? null : getCache(table);

      if (cache?.length) {
        response.value = cache;
      }

      return (async () => {
        const { res, err } = await getResource.value(table);

        if (res.value) {
          response.value = res.value.data;
          // add cache
          addCache(table, response.value);
          // set isEmpty to false
          isEmpty.value = response.value.data.length ? false : true;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // handle search (emitted from component)
    const handleSearch = (val) => {
      dismissPersistentAuthorizationNotice(store);
      // start loading
      isLoading.value = true;
      // set current Url
      currUrl.value = val.currUrl;
      // set current Hook
      currHook.value = val.currHook;
      // set search title
      searchTitle.value = val.searchTitle;
      hasActiveSearch.value = Boolean(val.hasActiveSearch);
      // get items from function
      getItemsFromApi(currUrl.value);
    };

    // switch page
    const handlePage = (page) => {
      dismissPersistentAuthorizationNotice(store);
      // start laoding
      isLoading.value = true;
      // go back up
      scrollToPageTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`);
    };

    // handle Edit
    const handleEdit = (encounter) => {
      dismissPersistentAuthorizationNotice(store);
      selectedEncounter.value = encounter;
      router.replace({ name: "auth", query: { encounterId: encounter.id } });
      scrollToPageTop();
    };

    const encounterMessageCount = (encounter) => {
      if (Number.isFinite(Number(encounter?.message_count))) {
        return Math.max(0, Number(encounter.message_count));
      }

      const messages = encounter?.stores?.encounter_messages;
      return Array.isArray(messages) ? messages.length : 0;
    };

    const encounterMessageLabel = (encounter) => {
      const count = encounterMessageCount(encounter);
      const unread = encounterUnreadMessageCount(encounter);
      const unreadLabel = unread ? `, ${unread} new` : "";
      return `${count} encounter message${count === 1 ? "" : "s"}${unreadLabel}`;
    };

    const encounterUnreadMessageCount = (encounter) => {
      return unreadEncounterMessageCount(store.state.user, encounter?.id);
    };

    const handleMessages = async (encounter) => {
      if (!encounter?.id) return;

      dismissPersistentAuthorizationNotice(store);
      await router.replace({
        name: "auth",
        query: { encounterId: encounter.id, tab: "messages" },
      });
      scrollToPageTop();
    };

    const syncSchemeFromDetail = (payload) => {
      const detailSchemeId = payload?.scheme?.id || payload?.scheme_id;
      const userSchemes = store.state?.user?.schemes;

      if (!detailSchemeId || !userSchemes?.length) {
        return false;
      }

      const currentScheme = userSchemes.find(x => x.curr);

      if (String(currentScheme?.id) === String(detailSchemeId)) {
        return false;
      }

      const nextScheme = userSchemes.find(x => String(x.id) === String(detailSchemeId));

      if (!nextScheme) {
        return false;
      }

      const user = store.state.user;
      user.schemes.forEach((x) => {
        x.curr = String(x.id) === String(detailSchemeId);
      });

      store.commit("loadUser", user);
      return true;
    };

    const handleInlineClose = (payload = null) => {
      const switchedScheme = syncSchemeFromDetail(payload);

      selectedEncounter.value = null;
      if (route.query.encounterId) {
        router.replace({ name: "auth" });
      }
      if (switchedScheme) {
        isLoading.value = true;
      }
      scrollToPageTop();
    };

    const handleInlineSaved = () => {
      selectedEncounter.value = null;
      if (route.query.encounterId) {
        router.replace({ name: "auth" });
      }
      if (currUrl.value) {
        isLoading.value = true;
        getItemsFromApi(currUrl.value);
      }
      scrollToPageTop();
    };

    const refreshRealtimeList = (event, page = 1) => {
      if (selectedEncounter.value || !currUrl.value || hasActiveSearch.value) {
        pendingRealtimeListPage.value = null;
        return false;
      }

      if (isLoading.value) {
        pendingRealtimeListPage.value = page;
        return true;
      }

      pendingRealtimeListPage.value = null;
      isLoading.value = true;
      error.value = null;
      getItemsFromApi(
        `${currUrl.value}${currHook.value || "&"}page=${page}`,
        { background: true }
      );
      return true;
    };

    watch(isLoading, loading => {
      if (!loading && pendingRealtimeListPage.value !== null) {
        refreshRealtimeList(null, pendingRealtimeListPage.value);
      }
    });

    const refreshRealtimeDetail = () => {
      return authInlineReview.value?.refreshFromServer?.() || false;
    };

    const refreshRealtimeMessages = () => {
      return authInlineReview.value?.refreshMessagesFromServer?.() || false;
    };

    const switchRealtimeScheme = async (schemeId) => {
      const user = store.state.user;
      const schemes = Array.isArray(user?.schemes) ? user.schemes : [];
      const selectedScheme = schemes.find(scheme => authSchemeId(scheme) === authSchemeId(schemeId));

      if (!selectedScheme) return false;

      store.commit("loadUser", {
        ...user,
        schemes: schemes.map(scheme => ({
          ...scheme,
          curr: authSchemeId(scheme) === authSchemeId(schemeId),
        })),
      });

      selectedEncounter.value = null;
      if (route.query.encounterId) {
        await router.replace({ name: "auth" });
      }

      showSearchFilters.value = true;
      searchTitle.value = null;
      hasActiveSearch.value = false;
      filterResetKey.value += 1;
      currUrl.value = authListUrl(authSchemeId(schemeId));
      currHook.value = "&";
      isLoading.value = true;
      getItemsFromApi(currUrl.value);
      scrollToPageTop();
      return true;
    };

    const realtimeTarget = {
      context: () => ({
        encounterId: selectedEncounter.value?.id || route.query.encounterId || null,
        hasSearch: hasActiveSearch.value,
        schemeId: defaultAuthSchemeId(),
        currentPage: Number(allPages.value?.current_page || 1),
      }),
      refreshList: refreshRealtimeList,
      refreshDetail: refreshRealtimeDetail,
      refreshMessages: refreshRealtimeMessages,
      switchScheme: switchRealtimeScheme,
    };
    const unregisterRealtimeTarget = registerAuthorizationRealtimeTarget("auth", realtimeTarget);
    onUnmounted(unregisterRealtimeTarget);

    watch(
      () => route.query.encounterId,
      (encounterId) => {
        selectedEncounter.value = encounterId ? { id: encounterId } : null;

        if (encounterId) {
          scrollToPageTop();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      if (store.state.realtimeAuthSchemeSwitch) {
        showSearchFilters.value = true;
        store.commit("queueRealtimeAuthSchemeSwitch", null);
      }

      if (!currUrl.value) {
        const schemeId = defaultAuthSchemeId();
        currUrl.value = authListUrl(schemeId);
        currHook.value = "&";
        getItemsFromApi(currUrl.value);
      }
    });

    // output results
    const encounters = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      toggleSearchFilters,
      hasPendingAuth,
      showSearchFilters,
      selectedEncounter,
      ageSex,
      showAgeSex,
      numberBadge,
      timeSince,
      handlePage,
      handleSearch,
      handleEdit,
      handleMessages,
      encounterMessageCount,
      encounterUnreadMessageCount,
      encounterMessageLabel,
      handleInlineClose,
      handleInlineSaved,
      isPostAddDetail,
      initialDetailState,
      isReady,
      isLoading,
      isEmpty,
      error,
      searchTitle,
      encounters,
      allPages,
      filterResetKey,
      authInlineReview,
    };
  },
};
</script>

<style scoped>
.auth-registry-page {
  box-sizing: border-box;
  margin: 0 auto;
  max-width: var(--ui-content-width);
  padding: 2rem 2rem 3rem;
  width: 100%;
}

.auth-registry-intro {
  border-bottom: 1px solid var(--ui-border);
  margin-bottom: 2rem;
  padding-bottom: 1.75rem;
}

.auth-registry-intro :deep(.ui-page-header) {
  margin-bottom: 0;
}

.auth-registry-intro :deep(.ui-page-header__title) {
  font-size: clamp(1.75rem, 2.1vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.auth-registry-intro :deep(.ui-page-header__description) {
  font-size: 1rem;
  line-height: 1.55;
}

.auth-add-link,
.auth-all-link,
.auth-filter-toggle {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-primary-foreground);
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0 0.9rem;
  text-decoration: none;
}

.auth-add-link:hover,
.auth-all-link:hover,
.auth-filter-toggle:hover {
  filter: brightness(0.95);
}

.auth-add-link:focus-visible,
.auth-all-link:focus-visible,
.auth-filter-toggle:focus-visible,
.auth-message-action:focus-visible,
.auth-request-action:focus-visible {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-registry-workbench {
  background: transparent;
  border: 0;
  box-shadow: none;
  min-width: 0;
}

.auth-registry-toolbar {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  padding-bottom: 1.25rem;
}

.auth-registry-summary {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.auth-registry-summary strong {
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.4;
}

.auth-registry-summary span {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.auth-filter-shell {
  border-bottom: 1px solid var(--ui-border);
  padding: 1.25rem 0;
}

.auth-filter-panel {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 0;
}

.auth-filter-panel :deep(.sectionx),
.auth-filter-panel :deep(form) {
  margin: 0;
  padding: 0;
}

.auth-filter-panel :deep(.sectionx > br) {
  display: none;
}

.auth-filter-panel :deep(.row) {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0;
}

.auth-filter-panel :deep(.col) {
  border: 0;
  min-width: 0;
  padding: 0 !important;
  width: 100% !important;
}

.auth-filter-panel :deep(.col:focus-within) {
  position: relative;
  z-index: 3;
}

.auth-filter-panel :deep(.col > label) {
  color: var(--ui-foreground);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 0.4rem;
}

.auth-filter-panel :deep(nav),
.auth-filter-panel :deep(.nav-wrapper),
.auth-filter-panel :deep(.input-field) {
  background: transparent !important;
  box-shadow: none !important;
  height: auto;
  line-height: normal;
  margin: 0;
  min-height: 2.75rem;
  overflow: visible !important;
  width: 100%;
}

.auth-filter-panel :deep(.input-field) {
  position: relative;
}

.auth-filter-panel :deep(.input-field > input),
.auth-filter-panel :deep(.select-wrapper input.select-dropdown) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  box-shadow: none !important;
  box-sizing: border-box;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem;
  font-weight: 400;
  height: 2.75rem !important;
  line-height: 2.75rem;
  margin: 0;
  padding: 0 2.5rem 0 0.75rem !important;
  width: 100%;
}

.auth-filter-panel :deep(.input-field > input[type="search"]::-webkit-search-cancel-button) {
  -webkit-appearance: none;
  display: none;
}

.auth-filter-panel :deep(.input-field > input:focus) {
  border-color: var(--ui-ring) !important;
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--ui-ring) 18%, transparent) !important;
}

.auth-filter-panel :deep(.input-field > .material-icons) {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--ui-muted-foreground) !important;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  height: 2.75rem;
  justify-content: center;
  line-height: 1;
  margin: 0;
  position: absolute;
  right: 0.25rem;
  top: 0;
  width: 2.25rem;
  z-index: 2;
}

.auth-filter-panel :deep(.input-field > .material-icons:hover) {
  color: var(--ui-foreground) !important;
}

.auth-filter-panel :deep(.native-autocomplete) {
  background: var(--ui-popover) !important;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: var(--ui-shadow-overlay);
  box-sizing: border-box;
  left: 0;
  margin: 0;
  max-height: min(16rem, 50vh);
  min-width: 0;
  overflow-y: auto;
  padding: 0.3rem;
  right: 0;
  top: calc(100% + 0.25rem);
  width: 100%;
  z-index: 220;
}

.auth-filter-panel :deep(.native-autocomplete li) {
  margin: 0;
  min-height: 0;
}

.auth-filter-panel :deep(.native-autocomplete li > :is(button, span)) {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: calc(var(--ui-radius) - 4px);
  color: var(--ui-popover-foreground) !important;
  cursor: pointer;
  display: flex;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.35;
  min-height: 2.35rem;
  padding: 0.55rem 0.65rem;
  text-align: left;
  width: 100%;
}

.auth-filter-panel :deep(.native-autocomplete li > button:hover),
.auth-filter-panel :deep(.native-autocomplete li > button:focus-visible),
.auth-filter-panel :deep(.native-autocomplete li > button[aria-selected="true"]) {
  background: var(--ui-accent) !important;
  color: var(--ui-accent-foreground) !important;
  outline: 0;
}

.auth-filter-panel :deep(.mobile-remote-sheet-field) {
  min-height: 2.75rem;
}

.auth-filter-panel :deep(.mobile-remote-trigger) {
  background: var(--ui-background) !important;
  border: 1px solid var(--ui-input) !important;
  border-radius: calc(var(--ui-radius) - 2px) !important;
  color: var(--ui-foreground) !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  height: 2.75rem !important;
  line-height: normal !important;
  padding: 0 0.25rem 0 0.75rem !important;
}

.auth-filter-panel :deep(.mobile-remote-trigger-main),
.auth-filter-panel :deep(.mobile-remote-trigger-icon) {
  height: 2.65rem !important;
  line-height: normal !important;
}

.auth-current-view {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  min-height: 3.5rem;
  padding: 0.75rem 0;
}

.summary-label {
  color: var(--ui-muted-foreground);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.summary-content {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.auth-current-view :deep(.chip),
.summary-chip {
  background: var(--ui-secondary) !important;
  border: 1px solid var(--ui-border) !important;
  border-radius: 999px !important;
  color: var(--ui-secondary-foreground) !important;
  font-size: 0.75rem !important;
  font-style: normal;
  font-weight: 500 !important;
  height: auto !important;
  line-height: 1.25 !important;
  margin: 0 !important;
  padding: 0.35rem 0.65rem !important;
  white-space: nowrap;
}

.auth-current-view :deep(.chip:first-child) {
  background: transparent !important;
  color: var(--ui-muted-foreground) !important;
}

.auth-current-view :deep(.chip i) {
  font-size: 0.8rem;
  line-height: 1;
}

.auth-progress {
  background: transparent;
  border: 0;
  border-radius: 0;
  height: 2px;
  margin: -2px 0 0;
  overflow: hidden;
}

.auth-request-list {
  background: transparent;
  border: 0;
  box-shadow: none;
  margin: 0;
  padding: 0;
}

.auth-request-head,
.auth-request-grid {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: 2.5rem minmax(14rem, 1.7fr) minmax(9rem, 1fr) minmax(10rem, 1.1fr) minmax(7rem, 0.75fr) 4.75rem 2.75rem;
}

.auth-request-head {
  border-bottom: 1px solid var(--ui-border);
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.8rem 0.75rem;
}

.auth-request-row {
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  padding: 1.15rem 0.75rem;
  transition: background-color 160ms ease;
}

.auth-request-row:hover,
.auth-request-row:focus-within {
  background: color-mix(in srgb, var(--ui-muted) 45%, transparent);
}

.auth-number {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  font-weight: 600;
}

.auth-person,
.auth-data-cell {
  min-width: 0;
}

.auth-person h6 {
  align-items: center;
  color: var(--ui-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.5rem;
  line-height: 1.4;
  margin: 0;
}

.auth-contact-line,
.auth-id-line {
  align-items: center;
  color: var(--ui-muted-foreground);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.4rem;
  line-height: 1.4;
  margin-top: 0.3rem;
}

.contact-dot {
  background: var(--ui-border);
  border-radius: 999px;
  display: inline-block;
  height: 0.25rem;
  width: 0.25rem;
}

.auth-id-line span,
.auth-data-cell > span:first-child {
  color: var(--ui-muted-foreground);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
}

.auth-id-line strong {
  color: var(--ui-foreground);
  font-weight: 600;
}

.auth-client {
  color: var(--ui-muted-foreground);
  font-size: 0.75rem;
  line-height: 1.4;
  margin: 0.25rem 0 0;
}

.auth-type-chip {
  align-items: center;
  background: var(--ui-secondary);
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  color: var(--ui-secondary-foreground);
  display: inline-flex;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1;
  padding: 0.25rem 0.45rem;
}

.auth-type-chip.is-principal {
  background: color-mix(in srgb, var(--ui-success) 10%, var(--ui-background));
  border-color: color-mix(in srgb, var(--ui-success) 24%, var(--ui-border));
  color: var(--ui-success);
}

.auth-status-pill {
  align-items: center;
  border: 1px solid var(--ui-border);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.6875rem;
  gap: 0.35rem;
  line-height: 1;
  padding: 0.35rem 0.55rem;
  white-space: nowrap;
}

.auth-status-pill strong {
  font-weight: 700;
  text-transform: uppercase;
}

.auth-status-pill span {
  font-weight: 600;
}

.auth-status-pill.is-waiting {
  background: #ffebee;
  border-color: #ffcdd2;
  color: var(--ui-destructive) !important;
}

.auth-status-pill.is-waiting :is(strong, span) {
  color: inherit !important;
}

.auth-status-pill.is-clear {
  background: var(--ui-secondary);
  color: var(--ui-muted-foreground);
}

.auth-data-cell p {
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  line-height: 1.4;
  margin: 0.25rem 0 0;
  overflow-wrap: anywhere;
}

.auth-message-cell,
.auth-action-cell {
  align-items: center;
  display: flex;
  justify-content: center;
}

.auth-message-action,
.auth-request-action {
  align-items: center;
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-muted-foreground);
  cursor: pointer;
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  min-width: 2.5rem;
  position: relative;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;
}

.auth-message-action {
  gap: 0.25rem;
  padding: 0 0.55rem;
}

.auth-message-action span {
  font-size: 0.6875rem;
  font-weight: 700;
}

.auth-message-action:hover,
.auth-message-action.has-unread {
  border-color: color-mix(in srgb, var(--ui-primary) 35%, var(--ui-border));
  color: var(--ui-primary);
}

.auth-message-unread {
  background: var(--ui-destructive);
  border: 2px solid var(--ui-background);
  border-radius: 999px;
  color: var(--ui-destructive-foreground);
  font-size: 0.55rem;
  line-height: 1;
  min-width: 1rem;
  padding: 0.2rem;
  position: absolute;
  right: -0.35rem;
  text-align: center;
  top: -0.35rem;
}

.auth-request-row:hover .auth-request-action,
.auth-request-row:focus-within .auth-request-action {
  background: var(--ui-foreground);
  border-color: var(--ui-foreground);
  color: var(--ui-background);
}

.auth-empty-state {
  align-items: center;
  border-bottom: 1px solid var(--ui-border);
  display: flex;
  flex-direction: column;
  padding: 3rem 1rem;
  text-align: center;
}

.auth-empty-state h3 {
  color: var(--ui-foreground);
  font-size: 1rem;
  font-weight: 650;
  margin: 0;
}

.auth-empty-state p {
  color: var(--ui-muted-foreground);
  font-size: 0.8125rem;
  margin: 0.4rem 0 0;
}

.auth-registry-workbench :deep(.auth-inline-detail) {
  background: transparent;
  border: 0 !important;
  border-radius: 0;
  box-shadow: none;
  box-sizing: border-box;
  max-width: 100%;
  overflow: visible;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-inline-detail *),
.auth-registry-workbench :deep(.auth-inline-detail *::before),
.auth-registry-workbench :deep(.auth-inline-detail *::after) {
  box-sizing: border-box;
}

.auth-registry-workbench :deep(.auth-inline-detail > div:not(.auth-inline-leader):not(.auth-created-guide)),
.auth-registry-workbench :deep(.auth-inline-detail > div:not(.auth-inline-leader):not(.auth-created-guide) > div),
.auth-registry-workbench :deep(.auth-inline-detail > div:not(.auth-inline-leader):not(.auth-created-guide) > div > .section) {
  margin-left: 0 !important;
  margin-right: 0 !important;
  max-width: 100% !important;
  min-width: 0;
  padding-left: 0 !important;
  padding-right: 0 !important;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-inline-inner-card) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  max-width: 100% !important;
  min-width: 0;
  overflow: visible;
  padding: 0 !important;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-inline-leader) {
  background: color-mix(in srgb, var(--ui-muted) 42%, var(--ui-background));
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  box-shadow: none;
  padding: 1.35rem 2rem !important;
}

.auth-registry-workbench :deep(.auth-inline-leader-main) {
  min-width: 0;
}

.auth-registry-workbench :deep(.auth-inline-close) {
  align-items: center;
  background: var(--ui-primary);
  border: 1px solid var(--ui-primary);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-primary-foreground);
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.4rem;
  justify-content: center;
  line-height: 1;
  min-height: 2.5rem;
  padding: 0 0.8rem;
  text-transform: none;
}

.auth-registry-workbench :deep(.auth-inline-close:hover),
.auth-registry-workbench :deep(.auth-inline-close:focus) {
  background: var(--ui-primary);
  border-color: var(--ui-primary);
  color: var(--ui-primary-foreground);
  filter: brightness(0.95);
  transform: none;
}

.auth-registry-workbench :deep(.auth-inline-close:focus-visible) {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-registry-workbench :deep(.auth-inline-close i) {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: inherit;
  font-size: 1rem;
  height: auto;
  line-height: 1;
  margin: 0;
  width: auto;
}

.auth-registry-workbench :deep(.auth-inline-close span) {
  color: var(--ui-primary-foreground) !important;
  display: inline-block;
  line-height: 1;
}

.auth-registry-workbench :deep(.auth-inline-switch) {
  border-bottom: 1px solid var(--ui-border) !important;
  gap: 0.25rem;
  justify-content: flex-start;
  margin: 0 !important;
  max-width: 100%;
  overflow-x: auto;
  padding: 0.65rem 1rem !important;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-inline-switch .tab-custom),
.auth-registry-workbench :deep(.auth-inline-switch .tab-custom-active) {
  font-size: 0.75rem;
  font-weight: 600;
  min-height: 3rem;
  padding: 0 1.2rem;
  text-transform: none;
}

.auth-registry-workbench :deep(.auth-created-guide) {
  background: color-mix(in srgb, #ecfdf5 58%, var(--ui-background));
  border: 1px solid var(--ui-border);
  border-left: 3px solid #0f766e;
  border-radius: calc(var(--ui-radius) - 2px);
  margin: 1rem 0 !important;
  max-width: 100%;
  padding: 0.8rem 0.9rem;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-created-guide-icon) {
  background: transparent;
  border: 0;
  color: #0f766e;
}

.auth-registry-workbench :deep(.auth-created-guide-copy p) {
  margin-top: 0.15rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  max-width: 100% !important;
  min-width: 0;
  overflow: visible;
  padding: 0 !important;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-diagnosis-card) {
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-radius: 0;
  gap: 0.75rem;
  margin: 0 !important;
  max-width: 100%;
  padding: 1.25rem 1rem;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-diagnosis-icon) {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: var(--ui-muted-foreground);
  height: 2rem;
  width: 2rem;
}

.auth-registry-workbench :deep(.auth-diagnosis-icon i) {
  font-size: 1.1rem;
}

.auth-registry-workbench :deep(.auth-diagnosis-copy span) {
  color: var(--ui-muted-foreground);
  font-size: 0.6875rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.auth-registry-workbench :deep(.auth-diagnosis-copy p) {
  color: var(--ui-foreground);
  font-size: 0.875rem;
  font-weight: 600;
}

.auth-registry-workbench :deep(.auth-add-item-action) {
  align-items: center;
  background: transparent;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  color: var(--ui-foreground);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--ui-font-sans);
  font-size: 0.8125rem;
  font-weight: 600;
  gap: 0.4rem;
  justify-content: center;
  min-height: 2.5rem;
  margin: 1rem 1rem 0;
  padding: 0 0.75rem;
}

.auth-registry-workbench :deep(.auth-add-item-action:hover) {
  background: var(--ui-accent);
  color: var(--ui-accent-foreground);
}

.auth-registry-workbench :deep(.auth-add-item-action:focus-visible) {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-registry-workbench :deep(.auth-add-item-action:disabled) {
  cursor: not-allowed;
  opacity: 0.5;
}

.auth-registry-workbench :deep(.auth-add-item-action.is-open) {
  border-color: color-mix(in srgb, var(--ui-destructive) 28%, var(--ui-border));
  color: var(--ui-destructive);
}

.auth-registry-workbench :deep(.auth-add-item-action i) {
  color: inherit !important;
  font-size: 1.1rem;
  line-height: 1;
}

.auth-registry-workbench :deep(.auth-inline-items-card),
.auth-registry-workbench :deep(.auth-inline-review-card > .auth-inline-items-card) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  margin: 1.5rem 0 0 !important;
  max-width: 100% !important;
  min-width: 0;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-inline-items-card > .card-content) {
  margin: 0 !important;
  max-width: 100%;
  padding: 0 1rem 1rem !important;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-inline-review-card .card-title) {
  color: var(--ui-foreground);
  display: block;
  font-size: 1rem;
  font-weight: 650;
  margin-bottom: 0.75rem;
}

.auth-registry-workbench :deep(.auth-mixed-section-heading.is-pending) {
  background: #fefce8;
  border: 0;
  border-bottom: 1px solid #fde68a;
  border-radius: 0;
  color: #713f12;
  padding: 0.75rem;
}

.auth-registry-workbench :deep(.auth-mixed-section-heading.is-pending :is(i, strong, small)) {
  color: #713f12;
}

.auth-registry-workbench :deep(.auth-inline-review-card .collection) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  list-style: none;
  margin: 0 !important;
  max-width: 100%;
  overflow: visible;
  padding-inline-start: 0;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-inline-review-card .collection-item) {
  background: transparent;
  border-bottom: 1px solid var(--ui-border);
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-line-item) {
  background: transparent !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  min-height: 3.5rem;
  margin: 0 !important;
  max-width: 100%;
  padding: 1.15rem 8rem 1.15rem 1rem;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-line-item.is-mixed-history-row) {
  background: transparent !important;
  margin-left: 0 !important;
  padding: 1.15rem 3.5rem 1.15rem 1rem !important;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-line-copy) {
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions) {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  gap: 0.25rem;
  margin: 0;
  padding: 0;
  right: 1rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions a) {
  background: var(--ui-background);
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 3px);
  color: var(--ui-muted-foreground);
  height: 2rem;
  text-decoration: none !important;
  width: 2rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions a:hover),
.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions a:focus-visible) {
  background: var(--ui-accent);
  color: var(--ui-accent-foreground);
  text-decoration: none !important;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions a:focus-visible) {
  outline: 2px solid var(--ui-ring);
  outline-offset: 2px;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions a + a::before) {
  display: none;
}

.auth-registry-workbench :deep(.auth-inline-review-card .line-item-actions i) {
  color: inherit !important;
  font-size: 1rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-mixed-action-zone) {
  background: transparent;
  border-bottom: 1px solid var(--ui-border);
  margin-left: 0 !important;
  margin-right: 0 !important;
  margin-top: 1.5rem;
  max-width: 100% !important;
  padding: 1.25rem 0 1.5rem;
  width: 100% !important;
}

.auth-registry-workbench :deep(.auth-mixed-action-heading) {
  margin-bottom: 0.75rem;
}

.auth-registry-workbench :deep(.auth-mixed-action-heading > span) {
  color: var(--ui-foreground);
}

.auth-registry-workbench :deep(.auth-mixed-action-heading i) {
  color: var(--ui-muted-foreground);
  font-size: 1rem;
}

.auth-registry-workbench :deep(.auth-submit-summary) {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  gap: 0;
  margin: 0.75rem 0 0;
}

.auth-registry-workbench :deep(.auth-submit-summary li) {
  background: transparent !important;
  border: 0 !important;
  border-bottom: 1px solid var(--ui-border) !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  min-height: 2.75rem;
  padding: 0.65rem 1rem;
}

.auth-registry-workbench :deep(.auth-submit-summary i) {
  height: 1.25rem;
  width: 1.25rem;
}

.auth-registry-workbench :deep(.auth-mixed-total-list) {
  margin: 1.25rem 0 1.5rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-total-row) {
  background: transparent;
  border-bottom: 1px solid var(--ui-border);
  border-top: 1px solid var(--ui-border);
  color: var(--ui-foreground);
  font-size: 0.8125rem;
  padding: 0.75rem 0;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-total-row .red-text) {
  color: var(--ui-foreground) !important;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-grand-total) {
  color: var(--ui-muted-foreground);
}

.auth-registry-workbench :deep(.auth-mixed-action-buttons) {
  margin: 0.75rem 0 0.5rem;
}

.auth-registry-workbench :deep(.auth-review-footer-actions) {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin: 1rem 0 0 !important;
  width: 100%;
}

.auth-registry-workbench :deep(.auth-review-footer-actions::after) {
  display: none;
}

.auth-registry-workbench :deep(.auth-review-footer-actions .auth-inline-back-action),
.auth-registry-workbench :deep(.auth-review-footer-actions .auth-submit-action) {
  float: none !important;
  margin: 0 !important;
  width: auto !important;
}

.auth-registry-workbench :deep(.auth-review-footer-actions .auth-submit-action) {
  margin-left: auto !important;
}

.auth-registry-workbench :deep(.auth-mixed-action-buttons .auth-inline-back-action),
.auth-registry-workbench :deep(.auth-mixed-action-buttons .auth-submit-action) {
  min-width: 0;
}

.auth-registry-workbench :deep(.auth-inline-back-action),
.auth-registry-workbench :deep(.auth-submit-action) {
  border-radius: calc(var(--ui-radius) - 2px);
  box-shadow: none;
  font-size: 0.8125rem;
  font-weight: 600;
  min-height: 2.5rem;
  padding: 0 0.9rem;
}

.auth-registry-workbench :deep(.auth-inline-back-action) {
  background: transparent;
  border: 1px solid var(--ui-border);
  color: var(--ui-foreground);
}

.auth-registry-workbench :deep(.auth-submit-action:not(:disabled):not(.disabled)) {
  background: #00695c !important;
  border-color: #00695c;
  color: #ffffff !important;
}

.auth-registry-workbench :deep(.auth-submit-action:not(:disabled):not(.disabled):hover),
.auth-registry-workbench :deep(.auth-submit-action:not(:disabled):not(.disabled):focus-visible) {
  background: #004d40 !important;
  border-color: #004d40;
  color: #ffffff !important;
}

.auth-registry-workbench :deep(.auth-inline-back-action i),
.auth-registry-workbench :deep(.auth-submit-action i.left) {
  font-size: 1rem;
  margin-right: 0.4rem;
}

.auth-registry-workbench :deep(.auth-inline-review-card .auth-mixed-section-heading.is-reviewed) {
  background: color-mix(in srgb, var(--ui-muted) 38%, transparent);
  border: 0;
  border-bottom: 1px solid var(--ui-border);
  border-top: 1px solid var(--ui-border);
  margin-left: 0 !important;
  margin-right: 0 !important;
  max-width: 100% !important;
  padding: 0.75rem;
  width: 100% !important;
}

@media (max-width: 600px) {
  .auth-registry-workbench :deep(.auth-inline-leader) {
    padding: 1.1rem 1rem !important;
  }

  .auth-registry-workbench :deep(.auth-diagnosis-card) {
    padding: 1rem;
  }

  .auth-registry-workbench :deep(.auth-inline-items-card > .card-content) {
    padding: 0 0.75rem 0.75rem !important;
  }

  .auth-registry-workbench :deep(.auth-inline-review-card .auth-line-item),
  .auth-registry-workbench :deep(.auth-inline-review-card .auth-line-item.is-mixed-history-row) {
    padding-left: 0.75rem !important;
    padding-right: 0 !important;
  }

  .auth-registry-workbench :deep(.auth-review-footer-actions) {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .auth-registry-workbench :deep(.auth-review-footer-actions .auth-inline-back-action),
  .auth-registry-workbench :deep(.auth-review-footer-actions .auth-submit-action) {
    justify-content: center;
    margin: 0 !important;
    width: 100% !important;
  }
}

@media (max-width: 1100px) {
  .auth-request-head,
  .auth-request-grid {
    gap: 0.75rem;
    grid-template-columns: 2rem minmax(12rem, 1.5fr) minmax(8rem, 0.9fr) minmax(9rem, 1fr) minmax(6rem, 0.7fr) 4.25rem 2.5rem;
  }
}

@media (min-width: 993px) {
  .auth-data-cell.is-provider > span:first-child,
  .auth-data-cell.is-diagnosis > span:first-child {
    display: none;
  }

  .auth-data-cell.is-provider > p,
  .auth-data-cell.is-diagnosis > p {
    margin-top: 0;
  }
}

@media (max-width: 992px) {
  .auth-filter-panel :deep(.row) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .auth-request-grid {
    align-items: start;
    grid-template-columns: 2rem minmax(0, 1fr) auto;
  }

  .auth-data-cell {
    grid-column: 2 / -1;
  }

  .auth-message-cell {
    grid-column: 2;
    justify-content: flex-start;
  }

  .auth-action-cell {
    grid-column: 3;
    grid-row: 1;
  }
}

@media (max-width: 600px) {
  .auth-registry-page {
    padding: 1.25rem 1rem 2rem;
  }

  .auth-registry-intro {
    margin-bottom: 1.5rem;
    padding-bottom: 1.25rem;
  }

  .auth-registry-toolbar {
    align-items: flex-start;
  }

  .auth-filter-panel :deep(.row) {
    grid-template-columns: minmax(0, 1fr);
  }

  .auth-current-view {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
  }

  .summary-content {
    justify-content: flex-start;
    text-align: left;
  }

  .auth-request-row {
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }

  .auth-request-grid {
    grid-template-columns: 1.5rem minmax(0, 1fr) 2.5rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-request-row,
  .auth-message-action,
  .auth-request-action {
    transition: none;
  }
}
</style>
